import dayjs from '@/dayjs'
import {useCallback, useEffect, useMemo} from 'react'
import {generatePath, useMatch, useNavigate, useOutletContext, useSearchParams} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {AgendaEntity, AgendaUrlParams, AgendaView} from '@/features/agenda/types'

interface UseAgendaNavigationReturn {
    /** Current navigation state from URL params */
    params: Omit<AgendaUrlParams, 'date'> & {date: Date}
    /** Navigate to a specific target (users/patients) */
    navigateToEntity: (entity: AgendaEntity) => void
    /** Navigate to a specific view (day/week/month/agenda) */
    navigateToView: (view: AgendaView) => void
    /** Navigate to a specific date */
    navigateToDate: (date: Date) => void
    /** Navigate with multiple params at once */
    navigateWithParams: (params: Partial<AgendaUrlParams>) => void
    /** Navigate to the next period based on current view */
    goToNextPeriod: () => void
    /** Navigate to the previous period based on current view */
    goToPreviousPeriod: () => void
    /** Navigate to today */
    goToToday: () => void
}

export const useAgendaNavigation = (): UseAgendaNavigationReturn => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const params = useOutletContext<AgendaUrlParams>()
    const match = useMatch(routes.AGENDA.path)

    // Memoize the base params to avoid unnecessary object creation
    const baseParams = useMemo(
        () => ({
            entity: params.entity ?? null,
            view: params.view ?? null,
            date: params.date ?? null
        }),
        [params.entity, params.view, params.date]
    )

    // Create memoized navigation functions that preserve search parameters
    const navigateWithParams = useCallback(
        (newParams: Partial<AgendaUrlParams>) => {
            const mergedParams = {
                ...baseParams,
                ...newParams
            }

            // Generate the base path with route params
            const basePath = generatePath(routes.AGENDA.path, mergedParams)

            // Preserve existing search parameters
            const currentSearchParams = new URLSearchParams(searchParams)

            // Navigate to the new path while preserving search parameters
            navigate(`${basePath}?${currentSearchParams.toString()}`)
        },
        [navigate, baseParams, searchParams]
    )

    const navigateToEntity = useCallback(
        (entity: AgendaEntity) => {
            navigateWithParams({entity})
        },
        [navigateWithParams]
    )

    const navigateToView = useCallback(
        (view: AgendaView) => {
            navigateWithParams({view})
        },
        [navigateWithParams]
    )

    const navigateToDate = useCallback(
        (date: Date) => {
            const formattedDate = dayjs(date).format('DD-MM-YYYY')
            navigateWithParams({date: formattedDate})
        },
        [navigateWithParams]
    )

    // Period navigation functions that work with URL params
    const goToNextPeriod = useCallback(() => {
        const currentDate = params.date ? dayjs(params.date, 'DD-MM-YYYY') : dayjs()
        const nextDate = currentDate.add(1, params.view == 'week' ? 'week' : params.view == 'month' ? 'month' : 'day')
        navigateToDate(nextDate.toDate())
    }, [params.date, params.view, navigateToDate])

    const goToPreviousPeriod = useCallback(() => {
        const currentDate = params.date ? dayjs(params.date, 'DD-MM-YYYY') : dayjs()
        const prevDate = currentDate.subtract(
            1,
            params.view == 'week' ? 'week' : params.view == 'month' ? 'month' : 'day'
        )
        navigateToDate(prevDate.toDate())
    }, [params.date, params.view, navigateToDate])

    const goToToday = useCallback(() => {
        navigateToDate(new Date())
    }, [navigateToDate])

    // If the target, view or date is not provided and we are on the agenda page, navigate to the default
    useEffect(() => {
        if (match && (!params.entity || !params.view || !params.date)) {
            navigateWithParams({
                entity: 'users',
                view: 'week',
                date: dayjs().format('DD-MM-YYYY')
            })
        }
    }, [params.entity, params.view, params.date, navigateWithParams, match])

    return {
        params: {...params, date: params.date ? dayjs(params.date, 'DD-MM-YYYY').toDate() : dayjs().toDate()},
        navigateToEntity,
        navigateToView,
        navigateToDate,
        navigateWithParams,
        goToNextPeriod,
        goToPreviousPeriod,
        goToToday
    }
}
